import { LegacyFriendlyDialog } from 'components/overlays';
import React, { useCallback, useEffect } from 'react';
import { SentryErrorBoundary } from 'utils/sentry';
import { Text2 } from '@el8/vital';
import useAppSelector from 'utils/hooks/useAppSelector';
import { useCloseRestoreDeletedPatientsDialog } from 'ui-modules/navbar';
import { logEvent } from 'utils/amplitude';
import { FeedbackType, useOpenSupportDialog } from 'ui-modules/support';
import { RestoreDeletedPatientsDialogContent } from './RestoreDeletedPatientsDialogContent';
import styles from './RestoreDeletedPatientsDialog.less';

export const RestoreDeletedPatientsDialog = (): JSX.Element => {
  const isOpen = useAppSelector((state) => state.navbar.restoreDeletedPatientsDialog.isOpen);
  const closeRestoreDeletedPatientsDialog = useCloseRestoreDeletedPatientsDialog();
  const openSupportDialog = useOpenSupportDialog();

  useEffect(
    function trackOpenDialogOnOpen() {
      if (isOpen) {
        logEvent('Open Restore Deleted Patients Dialog');
      }
    },
    [isOpen],
  );

  const handleContactSupport = useCallback(() => {
    logEvent('Contact Support From Restore Deleted Patients Dialog');
    openSupportDialog({ feedbackType: FeedbackType.RegularFeedbackType });
  }, [openSupportDialog]);

  return (
    <LegacyFriendlyDialog
      isOpen={isOpen}
      title="Recently Deleted Patients"
      width={850}
      className={styles.dialog}
      onClose={closeRestoreDeletedPatientsDialog}
      footer={
        <Text2 sizing="sm">
          {"Don't see what you're looking for? "}
          <Text2
            tag="a"
            href="https://help.elationhealth.com/articles/patient-chart-guide-recovering-deleted-charts"
            target="_blank"
            rel="noopener noreferrer"
            sizing="sm"
            onClick={() => logEvent('Learn More From Restore Deleted Patients Dialog')}
          >
            Learn More About Restoring Charts
          </Text2>
          {' or '}
          <Text2 tag="a" onClick={handleContactSupport} sizing="sm">
            Contact Elation Support
          </Text2>
        </Text2>
      }
    >
      <div className={styles.content}>
        {isOpen && (
          <SentryErrorBoundary>
            <RestoreDeletedPatientsDialogContent />
          </SentryErrorBoundary>
        )}
      </div>
    </LegacyFriendlyDialog>
  );
};
